/**
 * Export Tab Component for V4 Dashboard
 * Tabbed interface for CE Data, Credentials, and Survey exports
 */

import { useState, useCallback } from 'react'
import { Box, Card, Typography, Tab, Tabs } from '@mui/material'
import { FileDownload as ExportIcon } from '@mui/icons-material'
import { DashboardQueryParams } from '../../types'
import CEDataExportTab from './ce-data-export-tab'
import CredentialsExportTab from './credentials-export-tab'
import SurveyExportTab from './survey-export-tab'

interface ExportTabProps {
  queryParams: DashboardQueryParams
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  let { children, value, index } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`export-tabpanel-${index}`}
      aria-labelledby={`export-tab-${index}`}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  )
}

function a11yProps(index: number) {
  return {
    id: `export-tab-${index}`,
    'aria-controls': `export-tabpanel-${index}`,
  }
}

/**
 * Export Tab Component
 */
export default function ExportTab({ queryParams }: ExportTabProps) {
  let [activeTab, setActiveTab] = useState(0)

  let handleTabChange = useCallback(
    (event: React.SyntheticEvent, newValue: number) => {
      setActiveTab(newValue)
    },
    [],
  )

  return (
    <Card sx={{ p: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h6"
          sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
        >
          <ExportIcon />
          Data Export
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 800 }}
        >
          Export continuing education data, credentials, licenses, and survey
          results. Use the data grid toolbar to filter, search, and export to
          CSV format.
        </Typography>
      </Box>

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        aria-label="Export data tabs"
      >
        <Tab label="CE Data" {...a11yProps(0)} />
        <Tab label="Credentials & Licenses" {...a11yProps(1)} />
        <Tab label="Survey Results" {...a11yProps(2)} />
      </Tabs>

      <TabPanel value={activeTab} index={0}>
        <CEDataExportTab queryParams={queryParams} />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <CredentialsExportTab linkedServiceID={queryParams.linkedServiceID} />
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <SurveyExportTab linkedServiceID={queryParams.linkedServiceID} />
      </TabPanel>
    </Card>
  )
}
